/**
 * TranslateButton
 * Adds a live NL ↔ EN toggle using Google Translate Widget.
 * Used on Privacy Policy and Terms of Service pages for Google OAuth verification.
 */

import React, { useEffect, useState } from 'react';

declare global {
  interface Window {
    googleTranslateElementInit?: () => void;
    google?: any;
  }
}

const TranslateButton: React.FC = () => {
  const [translated, setTranslated] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Inject Google Translate widget script once
    if (document.getElementById('google-translate-script')) {
      setReady(true);
      return;
    }

    window.googleTranslateElementInit = () => {
      if (window.google?.translate?.TranslateElement) {
        new window.google.translate.TranslateElement(
          {
            pageLanguage: 'nl',
            includedLanguages: 'en',
            autoDisplay: false,
          },
          'google_translate_element_hidden'
        );
        setReady(true);
      }
    };

    const script = document.createElement('script');
    script.id = 'google-translate-script';
    script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    document.head.appendChild(script);
  }, []);

  const toEnglish = () => {
    const select = document.querySelector('.goog-te-combo') as HTMLSelectElement | null;
    if (select) {
      select.value = 'en';
      select.dispatchEvent(new Event('change'));
      setTranslated(true);
    }
  };

  const toDutch = () => {
    // Reset Google Translate cookie and reload to restore original Dutch
    const iframe = document.querySelector('.goog-te-banner-frame') as HTMLIFrameElement | null;
    if (iframe) {
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      const closeBtn = iframeDoc?.querySelector('.goog-close-link') as HTMLElement | null;
      closeBtn?.click();
    }
    // Fallback: clear cookie + reload
    document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=' + window.location.hostname;
    setTranslated(false);
    window.location.reload();
  };

  return (
    <>
      {/* Hidden Google Translate widget anchor */}
      <div id="google_translate_element_hidden" style={{ display: 'none' }} />

      {/* Visible toggle button */}
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', background: 'rgba(139,165,155,0.12)', borderRadius: '8px', padding: '3px', border: '1px solid rgba(139,165,155,0.25)' }}>
        <button
          onClick={translated ? toDutch : undefined}
          disabled={!translated}
          style={{
            padding: '5px 10px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: translated ? 400 : 600,
            border: 'none',
            cursor: translated ? 'pointer' : 'default',
            background: !translated ? '#8BA59B' : 'transparent',
            color: !translated ? 'white' : 'rgba(60,55,50,0.5)',
            transition: 'all 0.15s',
          }}
        >
          🇳🇱 NL
        </button>
        <button
          onClick={!translated ? toEnglish : undefined}
          disabled={!ready}
          style={{
            padding: '5px 10px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: translated ? 600 : 400,
            border: 'none',
            cursor: ready && !translated ? 'pointer' : 'default',
            background: translated ? '#8BA59B' : 'transparent',
            color: translated ? 'white' : ready ? 'rgba(60,55,50,0.5)' : 'rgba(60,55,50,0.25)',
            transition: 'all 0.15s',
          }}
        >
          🇬🇧 EN
        </button>
      </div>
    </>
  );
};

export default TranslateButton;
